import {
	FC,
	isValidElement,
	MutableRefObject,
	ReactNode,
	useEffect,
	useRef,
} from 'react';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { Backdrop, Stack, Typography } from '@mui/material';

import { CrossIcon } from '../../icons';

import { StyledDialog, StyledLoader } from './MplModal.styles';

export interface IMplModalProps {
	open: boolean;
	/**
	 * @requires @param testId for improved AQA experience.
	 * This approach ensures unique IDs by following the pattern:
	 * "ParentComponent-MountedComponent" + optional condition when multiple components are present.
	 * Example: "ParentComponent-MountedComponent-(!condition1 && condition2)"
	 */
	testId: string;
	icon?: ReactNode;
	title?: ReactNode;
	content?: ReactNode;
	onClose?: VoidFunction;
	/**
	 * @param onBackDropClick is just for closing modal by clicking backdrop instead of cross button
	 * if it wasn't provided onClose handler will be applied instead
	 */
	onBackDropClick?: VoidFunction;
	isLoading?: boolean;
	textAlign?: 'left' | 'right' | 'center';
	/**
	 * @param buttons is just <MplButton /> or multiple buttons wrapped into fragment </>
	 */
	buttons?: ReactNode;
	/**
	 * @param paperRef might be used to control paper behavior (scrolling for instance)
	 */
	paperRef?: MutableRefObject<HTMLDivElement | null>;
}

export const MplModal: FC<IMplModalProps> = ({
	open,
	icon,
	title,
	content,
	buttons,
	onClose,
	onBackDropClick,
	isLoading,
	textAlign,
	testId,
	paperRef,
}) => {
	const backButtonListener = useRef<PluginListenerHandle | null>(null);

	useEffect(() => {
		const removeListener = () => {
			if (backButtonListener.current) {
				backButtonListener.current.remove();
				backButtonListener.current = null;
			}
		};
		if (open) {
			if (!backButtonListener.current) {
				(async () => {
					backButtonListener.current = await App.addListener(
						'backButton',
						() => {
							if (onClose) {
								onClose();
							}
						}
					);
				})();
			} else {
				removeListener();
			}
		}
		return () => {
			removeListener();
		};
	}, [open, onClose]);

	return (
		<StyledDialog
			open={open}
			data-testid={testId}
			onClose={onBackDropClick || onClose}
			PaperProps={{ ref: paperRef, sx: { scrollBehavior: 'smooth' } }}
		>
			{onClose && (
				<Stack
					alignSelf="flex-end"
					onClick={onClose}
					sx={{ ':hover': { cursor: 'pointer' } }}
					data-testid={`${testId}-CrossCloseButton`}
				>
					<CrossIcon />
				</Stack>
			)}
			<Stack gap={3} width="100%">
				{icon && (
					<Stack
						data-testid={`${testId}-IconStack`}
						width="100%"
						alignItems="center"
						sx={{
							'& svg': { maxWidth: '100%', height: 'fit-content' },
						}}
					>
						{icon}
					</Stack>
				)}
				{title && (
					<Typography
						width="100%"
						variant="h3-sb"
						data-testid={`${testId}-TitleTypography`}
						textAlign={textAlign}
						children={title}
					/>
				)}

				{content &&
					(isValidElement(content) ? (
						content
					) : (
						<Typography
							data-testid={`${testId}-ContentTypography`}
							variant="body1"
							width="100%"
							textAlign={textAlign}
						>
							{content}
						</Typography>
					))}
				{buttons && (
					<Stack
						data-testid={`${testId}-ButtonStack`}
						gap={2}
						alignItems="center"
						width="100%"
					>
						{buttons}
					</Stack>
				)}
			</Stack>
			<Backdrop open={Boolean(isLoading)}>
				<StyledLoader data-testid={`${testId}-CircularProgress`} />
			</Backdrop>
		</StyledDialog>
	);
};
